import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PageHeading from "../../PageHeading";
import DynamicSelect from "../../DynamicSelect";
import { useTranslation } from "react-i18next";
import ImageUploader from "../../ImageUploader";
import { FiEdit2, FiSave } from "react-icons/fi";
import RichTextEditor from "../../RichTextEditor";

function sanitizeContent(content) {
  // Basic sanitization: removes script tags
  return content ? content.replace(
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    ""
  ) : "";
}

const AddBlog = ({ isSearchable }) => {
  const editor = useRef(null);
  const { t, i18n } = useTranslation();
  const [language, setLanguage] = useState("en");
  const [isSlugEditable, setIsSlugEditable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [categoriesList, setCategoriesList] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [imageUploaderKey, setImageUploaderKey] = useState(Date.now());

  // Stores all translation data for both languages
  const [translations, setTranslations] = useState({
    en: {
      metaTitle: "",
      metaDescription: "",
      title: "",
      slug: "",
      description: "",
      category: { id: "", name: "" },
      image: null,
    },
    bn: {
      metaTitle: "",
      metaDescription: "",
      title: "",
      slug: "",
      description: "",
      category: { id: "", name: "" },
      image: null,
    },
  });

  // State to hold actual content for each language
  const [contentEN, setContentEN] = useState("");
  const [contentBN, setContentBN] = useState("");

  // formData holds the data for the currently selected language's form inputs
  const [formData, setFormData] = useState({
    ...translations[language],
    content: language === 'en' ? contentEN : contentBN,
    image: null,
  });

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const MAX_CONTENT_LENGTH = 500000; // 500KB
  const CHUNK_SIZE = 1 * 1024 * 1024; // 1MB chunk size

  // Set i18n language to English on mount
  useEffect(() => {
    i18n.changeLanguage("en");
  }, [i18n]);

  // Update formData when language or translations change
  useEffect(() => {
    setFormData({
      ...translations[language],
      content: language === 'en' ? contentEN : contentBN,
      image: translations.en.image || translations.bn.image || null, // Prioritize English image, fallback to Bangla
    });
  }, [language, translations, contentEN, contentBN]);

  // Fetch categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        const response = await axios.get(
          "https://api.muktihospital.com/api/category",
          {
            headers: {
              "x-api-key":
                "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
            },
          }
        );
        setCategoriesList(response.data);
      } catch (error) {
        console.error("Error fetching categories:", error);
        toast.error(t("Failed to load categories."));
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCategories();
  }, [t]);

  // Auto-generate slug from English title if not manually edited
  useEffect(() => {
    if (!isSlugEditable) {
      const sourceTitle = translations.en.title;
      const generatedSlug = sourceTitle
        ? sourceTitle
            .toLowerCase()
            .replace(/\s+/g, "-")
            .replace(/[^\w\-]+/g, "") || ""
        : "";
      setTranslations((prev) => ({
        ...prev,
        en: { ...prev.en, slug: generatedSlug },
        bn: { ...prev.bn, slug: generatedSlug },
      }));
      setFormData((prev) => ({ ...prev, slug: generatedSlug }));
    }
  }, [translations.en.title, isSlugEditable, t]);

  // Memoized options for the category selector
  const categoryOptions = React.useMemo(
    () =>
      categoriesList.map((category) => {
        const categoryName =
          category.translations?.[language]?.name ||
          category.translations?.en?.name ||
          t("Unnamed Category");
        return {
          value: category.id,
          label: categoryName,
        };
      }),
    [categoriesList, language, t]
  );

  // Handles input changes for text fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setTranslations((prev) => ({
      ...prev,
      [language]: { ...prev[language], [name]: value },
    }));
  };

  // Handles image file upload
  const handleImageUpload = (file) => {
    if (file instanceof File) {
      const fileURL = URL.createObjectURL(file);
      setPreviewImage(fileURL);
      setFormData((prev) => ({ ...prev, image: file }));
      setTranslations((prev) => ({
        ...prev,
        en: { ...prev.en, image: file }, // Set image for both languages
        bn: { ...prev.bn, image: file },
      }));
    } else {
      setPreviewImage(null);
      setFormData((prev) => ({ ...prev, image: null }));
      setTranslations((prev) => ({
        ...prev,
        en: { ...prev.en, image: null },
        bn: { ...prev.bn, image: null },
      }));
    }
  };

  // Handles category selection from DynamicSelect
  const handleChange = (selectedOption) => {
    const category = { id: selectedOption.value, name: selectedOption.label };
    setFormData((prev) => ({ ...prev, category }));
    setTranslations((prev) => ({
      ...prev,
      [language]: { ...prev[language], category },
    }));
  };

  // Handles RichTextEditor content changes
  const handleContentChange = (newContent) => {
    if (newContent.length > MAX_CONTENT_LENGTH) {
      toast.warn(t(`Content exceeds ${MAX_CONTENT_LENGTH} characters. Please reduce.`));
    }
    if (language === 'en') {
      setContentEN(newContent);
    } else {
      setContentBN(newContent);
    }
    setFormData((prev) => ({ ...prev, content: newContent }));
  };

  // Toggles slug editability
  const handleSlugEditToggle = () => {
    setIsSlugEditable((prev) => !prev);
  };

  // Options for language selector
  const languageOptions = [
    { value: "en", label: t("English") },
    { value: "bn", label: t("Bangla") },
  ];

  // Handles language selection change
  const handleLanguageChange = (selectedOption) => {
    const newLanguage = selectedOption.value;
    setLanguage(newLanguage);
    i18n.changeLanguage(newLanguage);
  };

  // Function to upload large image files in chunks
  const uploadFileInChunks = async (file) => {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    let uploadedChunks = 0;

    for (let chunk = 0; chunk < totalChunks; chunk++) {
      const start = chunk * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunkFile = file.slice(start, end);

      const chunkFormData = new FormData();
      chunkFormData.append("chunk", chunkFile);
      chunkFormData.append("chunkNumber", chunk);
      chunkFormData.append("totalChunks", totalChunks);
      chunkFormData.append("originalFileName", file.name);

      try {
        const response = await fetch(
          "https://api.muktihospital.com/api/blogs/upload-chunk",
          {
            method: "POST",
            headers: {
              "x-api-key":
                "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
            },
            body: chunkFormData,
          }
        );

        if (!response.ok) {
          throw new Error(`Chunk upload failed at chunk ${chunk}`);
        }

        uploadedChunks++;
        toast.info(t(`Uploading chunk ${uploadedChunks}/${totalChunks}`));
      } catch (error) {
        toast.error(t(`Upload failed at chunk ${chunk}`));
        return false;
      }
    }
    return true;
  };

  // Validates only the currently active language's fields
  const validateCurrentLanguageData = (currentFormData, currentContent) => {
    const langName = language === 'en' ? 'English' : 'Bangla';

    if (
      !currentFormData.metaTitle.trim() ||
      !currentFormData.metaDescription.trim() ||
      !currentFormData.title.trim() ||
      !currentFormData.slug.trim() ||
      !currentFormData.description.trim()
    ) {
      toast.error(t(`Please fill in all required text fields for ${langName} content (Meta Title, Meta Description, Title, Slug, Description).`));
      return false;
    }

    if (!currentFormData.category?.id) {
      toast.error(t(`Please select a Category for ${langName} content.`));
      return false;
    }

    if (!currentContent.trim() || currentContent.trim().length < 10) {
      toast.error(t(`${langName} content is too short or empty.`));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    setError(null);

    // Step 1: Validate ONLY the currently selected language's data before proceeding
    const currentFormDataToValidate = translations[language];
    const currentContentToValidate = language === 'en' ? contentEN : contentBN;
    if (!validateCurrentLanguageData(currentFormDataToValidate, currentContentToValidate)) {
      setLoading(false);
      return;
    }

    try {
      const data = new FormData();

      // Step 2: Always append content for both languages
      data.append("en_content", sanitizeContent(contentEN));
      data.append("bn_content", sanitizeContent(contentBN));

      // Step 3: Prepare the translations object to send
      const translationsToSend = {
        en: {
          metaTitle: translations.en.metaTitle.trim(),
          metaDescription: translations.en.metaDescription.trim(),
          title: translations.en.title.trim(),
          slug: translations.en.slug.trim(),
          description: translations.en.description.trim(),
          category: translations.en.category || { id: "", name: "" },
        },
        bn: {
          metaTitle: translations.bn.metaTitle.trim(),
          metaDescription: translations.bn.metaDescription.trim(),
          title: translations.bn.title.trim(),
          slug: translations.bn.slug.trim(),
          description: translations.bn.description.trim(),
          category: translations.bn.category || { id: "", name: "" },
        },
      };

      data.append("translations", JSON.stringify(translationsToSend));

      // Step 4: Handle image upload
      if (formData.image) {
        const imageFile = formData.image;
        if (imageFile.size > MAX_FILE_SIZE) {
          const chunkUploadSuccess = await uploadFileInChunks(imageFile);
          if (!chunkUploadSuccess) {
            throw new Error(t("Chunk image upload failed"));
          }
        } else {
          data.append("image", imageFile);
        }
      }

      // Step 5: Send the request to the API
      const response = await fetch(
        "https://api.muktihospital.com/api/blogs/add",
        {
          method: "POST",
          headers: {
            "x-api-key":
              "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
          },
          body: data,
        }
      );

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || t("Failed to submit blog"));
      }

      toast.success(result.message || t("Blog added successfully"));
      setMessage(result.message || t("Blog added successfully"));

      // Step 6: Reset form states after successful submission
      setTranslations({
        en: {
          metaTitle: "",
          metaDescription: "",
          title: "",
          slug: "",
          description: "",
          category: { id: "", name: "" },
          image: null,
        },
        bn: {
          metaTitle: "",
          metaDescription: "",
          title: "",
          slug: "",
          description: "",
          category: { id: "", name: "" },
          image: null,
        },
      });
      setContentEN("");
      setContentBN("");
      setPreviewImage(null);
      setIsSlugEditable(false);
      setFormData({
        metaTitle: "",
        metaDescription: "",
        title: "",
        slug: "",
        description: "",
        category: { id: "", name: "" },
        content: "",
        image: null,
      });
      setImageUploaderKey(Date.now());
    } catch (err) {
      console.error("Submission error:", err);
      setError(err.message || t("An unexpected error occurred"));
      toast.error(err.message || t("Failed to submit blog"));
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbs = [
    { label: t("Dashboard"), url: "/" },
    { label: t("Manage Blogs") },
    { label: t("Add New Blog") },
  ];

  return (
    <div>
      <ToastContainer position="top-right" autoClose={3000} />
      <PageHeading title={t("Add New Blog")} breadcrumbs={breadcrumbs} />
      <div className="mb-4 flex justify-end">
        <DynamicSelect
          options={languageOptions}
          onChange={handleLanguageChange}
          value={languageOptions.find((option) => option.value === language)}
          isSearchable={isSearchable}
        />
      </div>
      <div>
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <div>
              <div className="bg-white shadow-sm rounded-lg pb-2">
                <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
                  <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                    {t("Basic Information")}
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-5">
                  <div>
                    <label className="label">{t("Title")}</label>
                    <input
                      type="text"
                      name="title"
                      placeholder={t("Title")}
                      value={formData.title}
                      onChange={handleInputChange}
                      className="input-field"
                      required
                    />
                  </div>
                  <div>
                    <label className="label">{t("Slug (Editable)")}</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        name="slug"
                        value={formData.slug}
                        onChange={handleInputChange}
                        className="input-field flex-1"
                        readOnly={!isSlugEditable}
                      />
                      <button
                        type="button"
                        onClick={handleSlugEditToggle}
                        className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md transition"
                        title={isSlugEditable ? t("Save Slug") : t("Edit Slug")}
                      >
                        {isSlugEditable ? (
                          <FiSave className="w-5 h-5" />
                        ) : (
                          <FiEdit2 className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="label">{t("Description")}</label>
                    <textarea
                      name="description"
                      placeholder={t("Short Description")}
                      value={formData.description}
                      onChange={handleInputChange}
                      className="input-field"
                      required
                    />
                  </div>
                  <div>
                    <label className="label">{t("Category")}</label>
                    <DynamicSelect
                      name="category"
                      value={{
                        value: formData.category.id,
                        label: formData.category.name,
                      }}
                      onChange={handleChange}
                      options={categoryOptions}
                      className="w-full p-3 border border-gray-300 rounded-md"
                      required
                    />
                  </div>
                </div>
                <div className="px-5 mb-5">
                  <label className="label">{t("Content")}</label>
                  <RichTextEditor
                    value={formData.content}
                    onChange={handleContentChange}
                  />
                </div>
              </div>
            </div>
            <div>
              <div className="bg-white shadow-sm rounded-lg">
                <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
                  <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                    {t("SEO Configuration")}
                  </h2>
                </div>
                <div className="p-5 grid grid-cols-2 gap-6">
                  <div>
                    <label className="label">{t("Meta Title")}</label>
                    <input
                      type="text"
                      name="metaTitle"
                      className="input-field"
                      placeholder={t("Enter Meta Title")}
                      value={formData.metaTitle}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label className="label">{t("Meta Description")}</label>
                    <input
                      type="text"
                      name="metaDescription"
                      className="input-field"
                      placeholder={t("Enter Meta Description")}
                      value={formData.metaDescription}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>
              <div className="bg-white shadow-sm rounded-lg mt-5">
                <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
                  <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                    {t("Upload Profile Photo")}
                  </h2>
                </div>
                <div className="p-5">
                  <ImageUploader
                    key={imageUploaderKey}
                    onFileUpload={handleImageUpload}
                  />
                  <p className="text-sm text-gray-600 mt-1">
                    {t("Max file size:")} {MAX_FILE_SIZE / 1024 / 1024}MB.{" "}
                    {t("Allowed types: JPEG, PNG, GIF")}
                  </p>
                </div>
              </div>
              <div className="mt-6 flex gap-4 justify-end">
                <button
                  type="submit"
                  className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
                  disabled={loading}
                >
                  {loading ? t("Submitting...") : t("Add Blog")}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddBlog;